/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Conversion service
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define(['underscore',
	'./ConversionService'
], function(_, ConversionService) {
	const conversionServiceInstance = new ConversionService();
	const ConverterInterface = conversionServiceInstance.getInterface();

	class ExploreToDashboardConverter extends ConverterInterface {
		constructor(definition) {
			super(Object.assign({
				src: 'EXPLORE',
				target: 'DASHBOARD'
			}, definition));
		}

		convert(src, target, spec) {
			return new Promise((resolve, reject) => {
				const specJson = JSON.parse(spec);
				if (!this.validate(specJson)) {
					reject(new Error('Invalid incoming spec'));
				}
				this.filterOutEmptyWidgets(specJson);
				resolve(JSON.stringify(this._doConvert(specJson)));
			});
		}

		validate(spec) {
			return !_.isEmpty(spec) && !_.isEmpty(this._getFilteredWidgets(spec));
		}

		filterOutEmptyWidgets(spec) {
			const filteredWidgets = this._getFilteredWidgets(spec);
			const filteredLayoutItems = this._getFilteredLayoutItems(spec.layout.items, filteredWidgets);
			spec.layout.items = filteredLayoutItems;
			spec.widgets = filteredWidgets;
		}

		_getFilteredWidgets(spec = {}) {
			const filteredWidgets = {};
			let widgets = [];
			if(spec.widgets) {
				widgets = Object.values(spec.widgets);
			} else if(spec.layout && spec.layout.items) {
				widgets = this._findWidgets(spec.layout.items);
			}

			widgets.forEach( widget => {
				if(widget.type === 'live'){
					filteredWidgets[widget.id] = widget;
				}
			});
			return filteredWidgets;
		}

		_findWidgets(items = []) {
			let widgets = [];
			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				if (item.items) {
					widgets = widgets.concat(this._findWidgets(item.items));
				} else if (item.features) {
					widgets.push(item.features['Models_internal']);
				}
			}
			return widgets;
		}

		_getFilteredLayoutItems(layoutItems, filteredWidgets) {
			return layoutItems.filter((item) => {
				if (item.subType === 'compare' ) {
					const firstWidgetId = item.items[0].id;
					const secondWidgetId = item.items[1].id;
					const hasLiveWidgets = filteredWidgets[firstWidgetId] || filteredWidgets[secondWidgetId];
					// Should only keep live widgets in comparison card layouts
					item.items = item.items.filter(widget => filteredWidgets[widget.id]);
					return hasLiveWidgets;
				} else {
					const widgetId = item.items[0].id;
					return filteredWidgets[widgetId];
				}
			});
		}

		_doConvert(exploreSpec) {
			const fragmentSpec = {
				dataSources: exploreSpec.dataSources,
				widgets: this._cleanWidgets(_.values(exploreSpec.widgets))
			};

			const convertedCards = this._convertCards(exploreSpec.layout.items);
			fragmentSpec.layout = convertedCards.layout;
			fragmentSpec.widgetToCardMap = convertedCards.widgetToCardMap;

			return fragmentSpec;
		}

		_cleanWidgets(widgets) {
			return widgets
				.map(widget => _.omit(widget, 'thumbnailId'));
		}

		_convertCards(cards) {
			return cards.reduce((acc, card) => this._convertCard(acc, card), {
				layout: [],
				widgetToCardMap: {}
			});
		}

		_convertCard(acc, card) {
			const widgets = this._getWidgets(card); //in order
			if (widgets.length === 1) {
				acc.layout.push(this._getGroupedItems(widgets)[0]);
				acc.widgetToCardMap[widgets[0].id] = card.id;
			} else {
				const groupId = _.uniqueId('group');
				acc.layout.push({
					id: groupId,
					type: 'group',
					relatedLayouts: '',
					items: this._getGroupedItems(widgets),
					style: {
						top: '0%',
						left: '0%',
						height:'50%',
						width: '50%'
					}
				});
				acc.widgetToCardMap[groupId] = card.id;
			}
			return acc;
		}

		_getWidgets(card) {
			const widgets = [];
			card.items.forEach(item => {
				if (item.type === 'widget') {
					widgets.push(item);
				}
			});
			return widgets;
		}

		_trimPercent(percent) {
			return (+(percent).toFixed(2));
		}

		_getGroupedItems(widgets) {
			var percentage = widgets.length > 1 ? 100 : 50;
			let width = this._trimPercent(1/widgets.length * percentage) + '%';
			return widgets.map((widget, index) => {
				const widgetSpec = {
					id: widget.id,
					type: 'widget',
					relatedLayouts: '',
					style: {
						top: '0%',
						left:  this._trimPercent(index/widgets.length * 100) + '%',
						height: percentage + '%',
						width: width
					},
					content: widget.content
				};
				if(widget.properties){
					//explore widget properties should be persisted after conversion.please find explore-2966
					widgetSpec.properties = widget.properties;
				}
				return widgetSpec;
			});
		}
	}

	return ExploreToDashboardConverter;
});
